/**
 * Cookies shared across the server subdomains (Domain=.<baseDomain>).
 *
 * dq_server: the user's server id. Read by the Cloudflare Worker on the bare
 * domain to route cold visits, and kept up to date here after login and on
 * server switches.
 *
 * dq_unit: one-shot stash used by the superadmin cross-server unit switch.
 * Set just before navigating to the other server's subdomain and applied
 * (then cleared) after the silent re-auth on arrival.
 */

import { getBaseDomain, SERVERS, ServerId } from "utils/server"

const cookieDomain = (): string | undefined => {
  const host = window.location.hostname
  if (host === "localhost" || host === "127.0.0.1") return undefined
  return `.${getBaseDomain()}`
}

const setCookie = (name: string, value: string, maxAge: number): void => {
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    "Path=/",
    "SameSite=Lax",
    `Max-Age=${maxAge}`,
  ]
  const domain = cookieDomain()
  if (domain) parts.push(`Domain=${domain}`)
  if (window.location.protocol === "https:") parts.push("Secure")
  document.cookie = parts.join("; ")
}

const getCookie = (name: string): string | null => {
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

export const setServerCookie = (server: ServerId): void =>
  setCookie("dq_server", server, 60 * 60 * 24 * 365)

export const getServerCookie = (): ServerId | null => {
  const value = getCookie("dq_server")
  return value && (SERVERS as readonly string[]).includes(value)
    ? (value as ServerId)
    : null
}

export const stashUnit = (unit: string): void => setCookie("dq_unit", unit, 300)

export const getStashedUnit = (): string | null => getCookie("dq_unit")

export const clearStashedUnit = (): void => setCookie("dq_unit", "", 0)
